import { parseLocalDate } from "./family-tree/dateAndTime";

/** Normalize to UTC calendar midnight (00:00:00Z) as epoch ms. */
function toUtcMidnightTimestamp(date) {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Convert a date string or timestamp to UTC calendar midnight epoch ms.
 * Use for family member API payloads (dob, date_of_death, wedding_anniversary, etc.)
 */
export function convertDateToTimestamp(dateValue) {
  if (dateValue === null || dateValue === undefined || dateValue === "") {
    return null;
  }

  const trimmed = String(dateValue).trim();
  if (!trimmed) return null;

  if (/^-?\d+$/.test(trimmed)) {
    const ts = parseInt(trimmed, 10);
    if (isNaN(ts)) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    return toUtcMidnightTimestamp(d);
  }

  const date = parseLocalDate(trimmed);
  if (!date) return null;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

// Convert Date to UTC midnight (00:00:00) epoch ms
export function convertDateToUTC(dateString) {
  if (!dateString) return null;

  const trimmed = String(dateString).trim();
  if (!trimmed) return null;

  if (/^-?\d+$/.test(trimmed)) {
    const ts = parseInt(trimmed, 10);
    if (isNaN(ts)) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  const date = parseLocalDate(trimmed) || new Date(trimmed);
  if (isNaN(date.getTime())) return null;
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

// Convert Time to UTC format
export function convertTimeToUTC(timeString) {
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 3600000 + minutes * 60000;
}

/**
 * Encode an event date for the API without local-timezone conversion.
 * Numeric ms values are sent as-is; YYYY-MM-DD is encoded as UTC calendar midnight.
 */
export function eventDateForApi(dateValue) {
  if (dateValue === null || dateValue === undefined || dateValue === "") return "";
  const trimmed = String(dateValue).trim();
  if (!trimmed) return "";

  if (/^-?\d+$/.test(trimmed)) return trimmed;

  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const y = parseInt(isoMatch[1], 10);
    const m = parseInt(isoMatch[2], 10);
    const d = parseInt(isoMatch[3], 10);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return "";
    return String(Date.UTC(y, m - 1, d));
  }

  const parsed = parseLocalDate(trimmed);
  if (!parsed) return "";
  return String(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

/**
 * Encode an event time for the API without local-timezone conversion.
 * Numeric ms values are sent as-is; HH:mm is encoded as ms offset from midnight.
 */
export function eventTimeForApi(timeValue) {
  if (timeValue === null || timeValue === undefined || timeValue === "") return "";
  const trimmed = String(timeValue).trim();
  if (!trimmed) return "";

  if (/^-?\d+$/.test(trimmed)) return trimmed;

  if (!trimmed.includes(":")) return "";
  const [hours, minutes] = trimmed.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return "";
  return String(hours * 3600000 + minutes * 60000);
}

// Convert UTC milliseconds to Date (YYYY-MM-DD) format
export function convertUTCToDate(utcMs) {
  if (!utcMs) return "";
  // IMPORTANT: Convert string "1764374400000" to Number 1764374400000
  const timestamp = Number(utcMs);
  if (isNaN(timestamp)) return "";

  const d = new Date(timestamp);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
  // return `${month}/${day}/${year}`;
}

// Convert UTC milliseconds to time (HH:mm) format
export function convertUTCToTime(utcMs) {
  if (utcMs === null || utcMs === undefined || utcMs === "") return "";
  // IMPORTANT: Convert string "72000000" to Number 72000000
  const timestamp = Number(utcMs);
  if (isNaN(timestamp)) return "";

  // Create date from Epoch + Duration
  const d = new Date(timestamp);
  const hours = String(d.getUTCHours()).padStart(2, "0");
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

// Convert UTC milliseconds to 12-hour time (e.g. "6:34pm")
export function convertUTCToTime12Hour(utcMs) {
  if (utcMs === null || utcMs === undefined || utcMs === "") return "";
  const timestamp = Number(utcMs);
  if (isNaN(timestamp)) return "";
  const d = new Date(timestamp);
  let hours = d.getUTCHours();
  const minutes = d.getUTCMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours || 12;
  const minutesStr = String(minutes).padStart(2, "0");
  return `${hours}:${minutesStr} ${ampm}`;
}
export function convertUTCToDateInvitation(utcMs) {
  if (!utcMs) return "";
  // IMPORTANT: Convert string "1764374400000" to Number 1764374400000
  const timestamp = Number(utcMs);
  if (isNaN(timestamp)) return "";

  const d = new Date(timestamp);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dayName = dayNames[d.getUTCDay()];
  // return `${year}-${month}-${day}`;
  return `${month}/${day}/${year} (${dayName})`;
}

/** Format backend date ms as MM/DD/YYYY using UTC calendar components (no local timezone conversion). */
export function formatMsToMMDDYYYY(ms) {
  if (ms === null || ms === undefined || ms === "") return "";
  const timestamp = Number(ms);
  if (isNaN(timestamp)) return "";

  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return "";

  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const year = d.getUTCFullYear();
  return `${month}/${day}/${year}`;
}

const UTC_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const UTC_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Weekday name from backend date ms using UTC (no local timezone conversion). */
export function formatMsToWeekday(ms) {
  if (ms === null || ms === undefined || ms === "") return "";
  const timestamp = Number(ms);
  if (isNaN(timestamp)) return "";
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return "";
  return UTC_WEEKDAY_NAMES[d.getUTCDay()] || "";
}

/** MM/DD/YYYY (Weekday) from backend date ms using UTC. */
export function formatMsToMMDDYYYYWithWeekday(ms) {
  const date = formatMsToMMDDYYYY(ms);
  if (!date) return "";
  const weekday = formatMsToWeekday(ms);
  return weekday ? `${date} (${weekday})` : date;
}

/** e.g. "July 7 (Wednesday)" from backend date ms using UTC. */
export function formatMsToMonthDayWeekday(ms) {
  if (ms === null || ms === undefined || ms === "") return "";
  const timestamp = Number(ms);
  if (isNaN(timestamp)) return "";
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return "";
  const month = UTC_MONTH_NAMES[d.getUTCMonth()];
  const day = d.getUTCDate();
  const weekday = UTC_WEEKDAY_NAMES[d.getUTCDay()];
  if (!month || !weekday) return "";
  return `${month} ${day} (${weekday})`;
}

/** e.g. "(Tuesday) 02" from backend date ms using UTC (month view display). */
export function formatMsToDayWeekdayDisplay(ms) {
  if (ms === null || ms === undefined || ms === "") return "";
  const timestamp = Number(ms);
  if (isNaN(timestamp)) return "";
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getUTCDate()).padStart(2, "0");
  const weekday = UTC_WEEKDAY_NAMES[d.getUTCDay()];
  return weekday ? `(${weekday}) ${day}` : day;
}

/** e.g. "(Tuesday) 02" from MM/DD/YYYY (optional weekday) using UTC. */
export function formatMMDDYYYYToDayWeekdayDisplay(dateString) {
  if (!dateString || typeof dateString !== "string") return "";
  const formatted = dateString.trim();
  const slashMatch = formatted.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s*\(([^)]+)\))?(.*)$/
  );
  if (!slashMatch) return "";
  const [, monthStr, dayStr, yearStr, existingWeekday, trailing] = slashMatch;
  const dayNum = dayStr.padStart(2, "0");
  let dayOfWeek = existingWeekday;
  if (!dayOfWeek) {
    const d = new Date(
      Date.UTC(
        parseInt(yearStr, 10),
        parseInt(monthStr, 10) - 1,
        parseInt(dayStr, 10)
      )
    );
    if (isNaN(d.getTime())) return "";
    dayOfWeek = UTC_WEEKDAY_NAMES[d.getUTCDay()];
  }
  return dayOfWeek
    ? `(${dayOfWeek}) ${dayNum}${trailing || ""}`
    : `${dayNum}${trailing || ""}`;
}